import React from "react";
import { useSearchParams } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import BatalNotaForm from "@/components/BatalNotaForm";
import { useAuth } from "@/context/AuthContext";
import { apiListNotas, apiGetNota, apiChangeNotaStatus, formatApiErrorDetail } from "@/lib/api";
import { Loader2, RefreshCw, Eye, X, CheckCircle2, Ban } from "lucide-react";
import { useCetak } from "@/hooks/usePengaturan";
import * as W from "@/lib/waktu";
import { qtyTeks } from "@/lib/format";

const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const STATUS_STYLE = {
  DRAFT: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  ISSUED: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  CANCELLED: "border-red-500/30 bg-red-500/10 text-red-300",
};

const BAYAR_LABEL = { BELUM_BAYAR: "Belum bayar", DP: "DP", LUNAS: "Lunas" };
const REFUND_LABEL = { MENUNGGU: "Menunggu pengembalian", SELESAI: "Pengembalian selesai", TIDAK_PERLU: "Tidak ada pembayaran" };

export default function NotaManagement() {
  const cetak = useCetak();
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [batal, setBatal] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  // owner 2026-10-15 (A1): filter and sort per Kasir/CS
  const [kasir, setKasir] = React.useState("");
  const [urut, setUrut] = React.useState("baru");
  // v2.13 (owner 2026-10-22): search box; "Buka nota" from Riwayat Pembayaran arrives as /nota?id=…
  const [cari, setCari] = React.useState("");
  const [sp, setSp] = useSearchParams();
  const bukaId = sp.get("id");
  React.useEffect(() => {
    if (bukaId) apiGetNota(bukaId).then(setDetail).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [bukaId]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await apiListNotas());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  const daftarKasir = React.useMemo(() => {
    const m = new Map();
    rows.forEach((r) => r.cs_id && m.set(r.cs_id, r.cs_nama || "-"));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const tampil = React.useMemo(() => {
    const needle = cari.trim().toLowerCase();
    const r = rows.filter((n) => (!kasir || n.cs_id === kasir) && (!needle || [n.nomor_nota, n.nomor_order, n.nomor_spk, n.customer_snapshot?.nama,
      ...(n.nomor_spks || [])].some((v) => (v || "").toLowerCase().includes(needle))));
    if (urut === "lama") return [...r].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    if (urut === "kasir") return [...r].sort((a, b) => (a.cs_nama || "~").localeCompare(b.cs_nama || "~"));
    return r;
  }, [rows, kasir, urut, cari]);

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(null), 2500); };

  const changeStatus = async (nota, status) => {
    setError(null);
    try {
      const updated = await apiChangeNotaStatus(nota.id, status);
      flash(`Nota ${nota.nomor_nota} → ${status}`);
      await load();
      if (detail?.id === nota.id) setDetail(updated);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="nota-management-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Manajemen Nota</h1>
          <p className="text-sm text-slate-400 mt-1">Dokumen transaksi dari SPK. Nominal memakai snapshot Order/SPK (bukan tarif master terbaru). Nota terbit hanya bisa dibatalkan Owner (alasan wajib).</p>
        </div>

        {notice && <div data-testid="nota-notice" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
        {error && <div data-testid="nota-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}

        {detail && (
          <div data-testid="nota-detail" className="rounded-xl border border-emerald-500/30 bg-slate-900/60 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-100">{detail.nomor_nota}</h2>
              <button data-testid="nota-detail-close" onClick={() => { setDetail(null); setBatal(false); if (bukaId) setSp({}); }} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
            </div>
            <div className="text-sm text-slate-400 grid grid-cols-2 sm:grid-cols-3 gap-y-1 gap-x-4">
              <span>Order: <span className="font-mono text-amber-300">{detail.nomor_order}</span></span>
              <span>SPK: <span className="font-mono text-amber-300">{detail.nomor_spk}</span></span>
              <span>Customer: <span className="text-slate-200">{detail.customer_snapshot?.nama}</span></span>
              <span>Pembuat: <span className="text-slate-200">{detail.dibuat_oleh_username}</span></span>
              <span>Status: <span className={`text-xs font-mono px-2 py-0.5 rounded border ${STATUS_STYLE[detail.status_nota]}`}>{detail.status_nota}</span></span>
            </div>

            <div>
              <h3 className="text-[13px] font-semibold text-slate-500 mb-2">Item</h3>
              <table className="w-full text-sm">
                <tbody>
                  {detail.items_snapshot.map((it, i) => (
                    <tr key={i} className="border-b border-slate-800/60">
                      <td className="py-2 text-slate-300">{it.kategori_produk}/{it.jenis_pekerjaan}{it.produk ? ` · ${it.produk}` : ""}</td>
                      <td className="py-2 text-right font-mono">{qtyTeks(it)}</td>
                      <td className="py-2 text-right font-mono">{rupiah(it.harga)}</td>
                      <td className="py-2 text-right font-mono text-slate-200">{rupiah(it.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end gap-6 text-sm font-mono mt-2">
                <span className="text-slate-400">Subtotal: <span className="text-slate-200">{rupiah(detail.subtotal_snapshot)}</span></span>
                <span className="text-slate-400">Diskon: <span className="text-slate-200">{rupiah(detail.diskon_snapshot)}</span></span>
                <span className="text-emerald-300 font-semibold" data-testid="nota-detail-total">Total: {rupiah(detail.total_snapshot)}</span>
              </div>
              {detail.catatan_snapshot && <p className="text-xs text-slate-500 mt-2">Catatan: {detail.catatan_snapshot}</p>}
            </div>

            {detail.pembatalan && (
              <div data-testid="nota-pembatalan" className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-slate-300 space-y-1">
                <div className="font-semibold text-red-300">Dibatalkan {W.waktu(detail.pembatalan.waktu)} oleh {detail.pembatalan.oleh?.nama || detail.pembatalan.oleh?.username}</div>
                <div>Alasan: {detail.pembatalan.alasan}</div>
                <div>Saat batal: {BAYAR_LABEL[detail.pembatalan.status_bayar_saat_batal] || detail.pembatalan.status_bayar_saat_batal}, dibayar {rupiah(detail.pembatalan.dibayar_saat_batal)}
                  {" · "}<span data-testid="nota-pengembalian-status">{REFUND_LABEL[detail.pembatalan.pengembalian?.status] || detail.pembatalan.pengembalian?.status}</span>
                  {detail.pembatalan.pengembalian?.status === "MENUNGGU" && ` (sisa ${rupiah(detail.pembatalan.pengembalian.saldo)})`}</div>
                {isOwner && detail.pembatalan.pengembalian?.status !== "TIDAK_PERLU" && (
                  <a data-testid="nota-ke-pengembalian" href={`/pembayaran?pengembalian=${detail.id}`} className="inline-block text-xs text-amber-300 hover:underline">Buka pengembalian →</a>
                )}
              </div>
            )}

            {batal && <BatalNotaForm notaId={detail.id} onCancel={() => setBatal(false)}
              onDone={async () => { setBatal(false); flash(`Nota ${detail.nomor_nota} dibatalkan`); await load(); setDetail(await apiGetNota(detail.id)); }} />}

            <div className="flex gap-2 pt-2 border-t border-slate-800">
              {isOwner && detail.status_nota === "ISSUED" && !batal && (
                <button data-testid="nota-batal-owner" onClick={() => setBatal(true)} className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/10"><Ban size={15} /> Batalkan nota</button>
              )}
              {detail.status_nota === "DRAFT" && (
                <button data-testid="nota-issue" onClick={() => changeStatus(detail, "ISSUED")} className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400"><CheckCircle2 size={15} /> Terbitkan (ISSUED)</button>
              )}
              {detail.status_nota === "DRAFT" && (
                <button data-testid="nota-cancel" onClick={() => changeStatus(detail, "CANCELLED")} className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/10"><Ban size={15} /> Batalkan</button>
              )}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-800">
            <h2 className="mr-auto text-[15px] font-bold text-soq-biru-tua">Daftar Nota <span className="text-xs font-normal text-slate-500">({tampil.length})</span></h2>
            <input data-testid="nota-cari" value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Cari nota / order / SPK / customer"
              className="w-full sm:w-64 rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm" />
            <select data-testid="nota-filter-kasir" value={kasir} onChange={(e) => setKasir(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm">
              <option value="">Semua Kasir/CS</option>{daftarKasir.map(([id, nama]) => <option key={id} value={id}>{nama}</option>)}
            </select>
            <select data-testid="nota-urut" value={urut} onChange={(e) => setUrut(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm">
              <option value="baru">Urut: Terbaru</option><option value="lama">Urut: Terlama</option><option value="kasir">Urut: Kasir/CS (A–Z)</option>
            </select>
            <button data-testid="nota-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
          ) : (
            <div className="overflow-x-auto">
              <p className="px-4 pt-2 text-[11px] text-slate-500 sm:hidden">Geser tabel ke kanan untuk melihat tombol; kolom Aksi tetap menempel di kanan.</p>
              <table className="w-full text-sm" data-testid="nota-table">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    <th className="px-4 py-3">No. Nota</th>
                    <th className="px-4 py-3">No. Order</th>
                    <th className="px-4 py-3">No. SPK</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Kasir/CS</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="sticky right-0 bg-slate-900 px-4 py-3 text-right shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.25)]">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {tampil.map((n) => (
                    <tr key={n.id} data-testid={`nota-row-${n.nomor_nota}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-3 font-mono text-emerald-300">{n.nomor_nota}</td>
                      <td className="px-4 py-3 font-mono text-slate-300">{n.nomor_order}</td>
                      <td className="px-4 py-3 font-mono text-slate-300">{n.nomor_spk || (n.nomor_spks || []).join(", ")}</td>
                      <td className="px-4 py-3 text-slate-300">{n.customer_snapshot?.nama || "—"}</td>
                      <td data-testid={`nota-cs-${n.nomor_nota}`} className="px-4 py-3 text-xs text-slate-300">{n.cs_nama || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-200">{rupiah(n.total_snapshot)}</td>
                      <td className="px-4 py-3"><span data-testid={`nota-status-${n.nomor_nota}`} className={`text-xs font-mono px-2 py-1 rounded-md border ${STATUS_STYLE[n.status_nota]}`}>{n.status_nota}</span>
                        {n.status_nota === "ISSUED" && <span data-testid={`nota-bayar-status-${n.nomor_nota}`} className="ml-1 text-[10px] font-mono text-slate-400">{(n.status_bayar || "BELUM_BAYAR").replace("_", " ")}</span>}</td>
                      <td className="sticky right-0 bg-white px-4 py-3 text-right whitespace-nowrap space-x-1 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.25)]">
                        {n.status_nota === "ISSUED" && <a data-testid={`nota-bayar-${n.nomor_nota}`} href={`/pembayaran?nota=${n.id}`} className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-emerald-500/40 hover:text-emerald-300">Bayar</a>}
                        {n.status_nota === "ISSUED" && n.jenis_transaksi && <a data-testid={`nota-invoice-${n.nomor_nota}`} href={`/nota/${n.id}/invoice`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Invoice</a>}
                        {cetak.nota && n.status_nota !== "DRAFT" && <a data-testid={`nota-cetak-${n.nomor_nota}`} href={`/nota/${n.id}/cetak`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Cetak</a>}
                        <button data-testid={`nota-view-${n.nomor_nota}`} onClick={async () => { setBatal(false); setDetail(await apiGetNota(n.id)); }} className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-emerald-500/40 hover:text-emerald-300"><Eye size={13} /></button>
                      </td>
                    </tr>
                  ))}
                  {tampil.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{rows.length ? "Tidak ada nota yang cocok." : "Belum ada Nota."}</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
